import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Spin, Select, InputNumber, Divider, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchCounties,
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
} from "../../../features/fetch/fetchSlice";
import CardLoading from "../../../components/CardLoading";
import { schReclStatusData } from "../../../data";

const CropBandAdd = () => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const urlCrop = "main_crop/fetch_maincrops";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const { saving } = useSelector((state) => state.save);
  const { counties, subCounties, locations, subLocations } = useSelector(
    (state) => state.fetch
  );
  const {
    loading,
    activeObjCropBand,
    dataCrop,
    dataPathway,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjCropBand: state.obj.activeObj["cropBand"],
    dataCrop: state.fetch.data[urlCrop] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/trapezoidal-bund-for-crops");
  }

  const [cropCountyId, setCropCountyId] = useState(
    activeObjCropBand?.cropCountyId ?? ""
  );
  const [cropConstId, setCropConstId] = useState(
    activeObjCropBand?.cropConstId ?? ""
  );
  const [cropSubCountyId, setCropSubCountyId] = useState(
    activeObjCropBand?.cropSubCountyId ?? ""
  );
  const [cropLocationId, setCropLocationId] = useState(
    activeObjCropBand?.cropLocationId ?? ""
  );
  const [cropSubLocationId, setCropSubLocationId] = useState(
    activeObjCropBand?.cropSubLocationId ?? ""
  );
  const [cropWardId, setCropWardId] = useState(
    activeObjCropBand?.cropWardId ?? ""
  );

  const handleCropCountyChange = async (value) => {
    setCropCountyId(value);
    setCropConstId("");
    setCropSubCountyId("");
    setCropLocationId("");
    setCropSubLocationId("");
    setCropWardId("");

    form.setFieldsValue({
      cropCountyId: value,
      cropConstId: "",
      cropSubCountyId: "",
      cropLocationId: "",
      cropSubLocationId: "",
      cropWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(
      fetchData({
        url: urlConstituency,
        constCountyId: value,
      })
    );
  };

  const handleCropConstituencyChange = async (value) => {
    setCropConstId(value);
    setCropWardId("");

    form.setFieldsValue({
      cropConstId: value,
      cropWardId: "",
    });

    await dispatch(
      fetchData({
        url: urlWard,
        wdConstId: value,
      })
    );
  };

  const handleCropSubCountyChange = async (value) => {
    setCropSubCountyId(value);
    setCropLocationId("");
    setCropSubLocationId("");

    form.setFieldsValue({
      cropSubCountyId: value,
      cropLocationId: "",
      cropSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleCropLocationChange = async (value) => {
    setCropLocationId(value);
    setCropSubLocationId("");

    form.setFieldsValue({
      cropLocationId: value,
      cropSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleCropSubLocationChange = (value) => {
    setCropSubLocationId(value);
    form.setFieldsValue({ cropSubLocationId: value });
  };

  const handleCropWardChange = (value) => {
    setCropWardId(value);
    form.setFieldsValue({ cropWardId: value });
  };

  const onFinish = async (data) => {
    data.cropId = activeObjCropBand?.cropId ? activeObjCropBand?.cropId : null;
    data.cropCropsId = Array.isArray(data?.cropCropsId)
      ? data?.cropCropsId.join(",")
      : data?.cropCropsId;

    data.cropPathwayId = Array.isArray(data?.cropPathwayId)
      ? data?.cropPathwayId.join(",")
      : data?.cropPathwayId;
    const res = await dispatch(
      save({
        ...data,
        url: "crop_band/save_cropband",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjCropBand,
    });
    if (activeObjCropBand?.cropCropsId) {
      form.setFieldsValue({
        cropCropsId: activeObjCropBand?.cropCropsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjCropBand?.cropPathwayId) {
      form.setFieldsValue({
        cropPathwayId: activeObjCropBand?.cropPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjCropBand, form]);

  const initialFormValues = {
    ...activeObjCropBand,
  };

  useEffect(() => {
    dispatch(fetchCounties());
    dispatch(
      fetchData({
        url: urlCrop,
        limit: null,
        start: 0,
      })
    );
  }, []);

  useEffect(() => {
    if (activeObjCropBand?.cropId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjCropBand?.cropCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjCropBand?.cropCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjCropBand?.cropConstId,
        })
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjCropBand?.cropSubCountyId,
        })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjCropBand?.cropLocationId,
        })
      );
    }
  }, [activeObjCropBand, form]);

  if (loading) return <CardLoading />;

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Crop Band List", path: "/rob/trapezoidal-bund-for-crops" },
          { title: "Crop Band Add Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="w-full">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title !bg-white !px-0">
              Trapezoidal Bund For Crops Details
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="cropName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="cropStatus"
                className=""
                label={"Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    schReclStatusData?.length > 0 &&
                    schReclStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="cropCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleCropCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {cropCountyId && (
                <>
                  <Form.Item
                    name="cropConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleCropConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="cropSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleCropSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {cropSubCountyId && (
                <Form.Item
                  name="cropLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleCropLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {cropLocationId && (
                <Form.Item
                  name="cropSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleCropSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {cropConstId && (
                <Form.Item
                  name="cropWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleCropWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="cropLat"
                label={"Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="cropLong"
                label={"Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
               
              <Form.Item
                name="cropSize"
                label={"Size (Acres)"}
                rules={[
                  {
                    required: true,
                    message: "Please add size",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="cropGroundSlope"
                label={"Slope of The Ground"}
                rules={[
                  {
                    required: true,
                    message: "Please add slope of the ground",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="cropMaxWaterDepth"
                label={"Depth of water"}
                rules={[
                  {
                    required: true,
                    message: "Please add depth of water",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="cropCultivationArea"
                label={"Area of Land Under Cultivation"}
                rules={[
                  {
                    required: true,
                    message: "Please add Area of land under cultivation",
                  },
                ]}
              >
                <InputNumber
                  suffix={"Acres"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="cropMaxBandHeight"
                label={"Height of The Bund"}
                rules={[
                  {
                    required: true,
                    message: "Please add Maximum height of the bund",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="cropCatchmentArea"
                label={"Catchment Area"}
                rules={[
                  {
                    required: true,
                    message: "Please add catchment area",
                  },
                ]}
              >
                <InputNumber
                  suffix={"M"}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                label={"Crops type (Proposed/Existing)"}
                name="cropCropsId"
                rules={[
                  {
                    required: true,
                    message: "Required field",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following crops
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataCrop?.length > 0 &&
                    dataCrop?.map((item) => (
                      <Select.Option key={item?.cropId} value={item?.cropId}>
                        {item?.cropName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="cropPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjCropBand?.cropId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default CropBandAdd;
